import type { IHoliday } from "@/interface/dashboard.interface";
import {
  formatDate,
  getDaysLeftForDate,
  getDaysLeftStyle,
} from "@/utility/helper";
import { Calendar } from "lucide-react";

const RenderHolidaysTable: React.FC<{ holidays: IHoliday[] }> = ({
  holidays,
}) => {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <h3 className="font-display text-sm font-semibold text-slate-800 flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-emerald-500" />
          </span>
          Upcoming Holidays
        </h3>
        <span className="text-xs text-slate-400">
          {holidays.length} upcoming
        </span>
      </div>
      <div className="p-2 sm:p-3">
        {holidays.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Holiday
                  </th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Day
                  </th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Days Left
                  </th>
                </tr>
              </thead>
              <tbody>
                {holidays.map((holiday, index) => {
                  const daysLeft = getDaysLeftForDate(holiday.date);
                  const isToday = daysLeft === 0;
                  const dateObj = new Date(holiday.date);
                  const dayName = dateObj.toLocaleDateString("en-US", {
                    weekday: "long",
                  });
                  return (
                    <tr
                      key={index}
                      className={`group ${isToday ? "bg-amber-50/50" : ""}`}
                    >
                      <td className="px-4 py-3 border-t border-slate-100 group-hover:bg-slate-50/80 rounded-l-lg">
                        <span className="text-slate-700 font-medium">
                          {holiday.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 border-t border-slate-100 text-slate-500 text-xs group-hover:bg-slate-50/80">
                        {formatDate(holiday.date)}
                      </td>
                      <td className="px-4 py-3 border-t border-slate-100 text-slate-500 text-xs group-hover:bg-slate-50/80">
                        {dayName}
                      </td>
                      <td className="px-4 py-3 border-t border-slate-100 group-hover:bg-slate-50/80 rounded-r-lg">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold tabular-nums ${
                            isToday
                              ? "bg-amber-100 text-amber-700"
                              : getDaysLeftStyle(daysLeft)
                          }`}
                        >
                          {isToday
                            ? "Today"
                            : daysLeft > 0
                              ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""}`
                              : "Passed"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-slate-500 py-8">
            No upcoming holidays
          </div>
        )}
      </div>
    </div>
  );
};

export default RenderHolidaysTable;